/**
 * NotificationPanel component
 * Dropdown panel displaying notification list with mark-all-read functionality
 */

import { useEffect, useRef } from 'react'
import { CheckCheck, BellOff } from 'lucide-react'
import { useRealtimeNotifications } from '../../dashboard/hooks/use-realtime-notifications'
import { useMarkAllRead } from '../hooks/use-mark-all-read'
import { NotificationItem } from './notification-item'
import type { Notification } from '../../dashboard/types/dashboard.types'

interface NotificationPanelProps {
  open: boolean
  onClose: () => void
}

export function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const { notifications, markAsRead, loading } = useRealtimeNotifications()
  const { markAllRead, loading: markAllLoading } = useMarkAllRead()

  // Close on outside click
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, onClose])

  // Close on Escape key
  useEffect(() => {
    if (!open) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onClose])

  const handleMarkAllRead = async () => {
    await markAllRead()
    // Notifications will be updated via realtime subscription
  }

  const handleNavigate = (notification: Notification) => {
    // Handle navigation based on entity_type and entity_id
    // This can be implemented based on routing structure
    console.log('Navigate to:', notification.entity_type, notification.entity_id)
    onClose()
  }

  if (!open) return null

  return (
    <div className="notification-panel" ref={panelRef}>
      <div className="notification-panel-header">
        <h3 className="notification-panel-title">Notifications</h3>
        {notifications.length > 0 && (
          <button
            className="notification-panel-action"
            onClick={handleMarkAllRead}
            disabled={markAllLoading}
            title="Mark all as read"
          >
            <CheckCheck size={16} />
            <span>Mark all read</span>
          </button>
        )}
      </div>

      <div className="notification-panel-body">
        {loading && notifications.length === 0 ? (
          <div className="notification-empty">
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-empty">
            <BellOff size={48} />
            <p>No notifications</p>
          </div>
        ) : (
          <div className="notification-list">
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={markAsRead}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
